import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Loader2, Play, Trash2, X } from "lucide-react";
import { getBackendUrl, authHeaders } from "../lib/http";

// Eklenti gorevleri: panelden verilir, "Panelden görev al" acik eklenti (or. VDS) dakikada bir alip calistirir.
// API: routes/tasks.ts. Eklenti: background/tasks.ts

type Task = {
  id: string; type: "scan" | "analyze"; title: string; status: string; repeatDaily: boolean; runAt: string;
  startedAt: string | null; finishedAt: string | null; heartbeatAt: string | null; error: string | null;
  payload: Record<string, unknown>;
  progress: { message?: string; keyword?: string; kwIndex?: number; kwTotal?: number; page?: number; pages?: number; scanned?: number; saved?: number; deepDone?: number; deepTotal?: number; log?: string[] } | null;
};
type Worker = { workerId: string; at: string; version: string | null } | null;

const INPUT = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300";
const LABEL = "mb-1 block text-xs font-medium text-gray-700";
const STATUS: Record<string, [string, string]> = {
  pending: ["Bekliyor", "bg-gray-100 text-gray-600"],
  running: ["Çalışıyor", "bg-indigo-50 text-indigo-700"],
  done: ["Bitti", "bg-emerald-50 text-emerald-700"],
  failed: ["Hata", "bg-red-50 text-red-600"],
  cancelled: ["İptal", "bg-gray-100 text-gray-500"],
};

const ago = (iso: string | null) => {
  if (!iso) return "—";
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? "az önce" : m < 60 ? `${m} dk önce` : m < 1440 ? `${Math.round(m / 60)} saat önce` : `${Math.round(m / 1440)} gün önce`;
};
const when = (iso: string) => new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const numOrNull = (v: string) => (v.trim() === "" || isNaN(Number(v)) ? null : Number(v));

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/tasks${path}`, { ...init, headers: { ...authHeaders(), "Content-Type": "application/json" } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? `İstek başarısız (${r.status})`);
  return j as T;
}

export default function TasksPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api<{ tasks: Task[]; worker: Worker }>(""),
    refetchInterval: 15000,
  });

  const [keywords, setKeywords] = useState("");
  const [pages, setPages] = useState("2");
  const [deep, setDeep] = useState(true);
  const [ageMax, setAgeMax] = useState("30");
  const [favMin, setFavMin] = useState("");
  const [viewsMin, setViewsMin] = useState("");
  const [negative, setNegative] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [time, setTime] = useState("08:00");

  const create = useMutation({
    mutationFn: () => {
      // Tekrarli gorev: bugun o saat gecmisse yarin; tekrarsiz: hemen
      let runAt = new Date();
      if (repeat) {
        const [h, m] = time.split(":").map(Number);
        runAt = new Date(); runAt.setHours(h || 0, m || 0, 0, 0);
        if (runAt <= new Date()) runAt.setDate(runAt.getDate() + 1);
      }
      return api<{ task: Task }>("", {
        method: "POST",
        body: JSON.stringify({
          type: "scan", repeatDaily: repeat, runAt: runAt.toISOString(),
          payload: {
            keywords: keywords.split(/\r?\n/).map((k) => k.trim()).filter(Boolean),
            pages: Number(pages) || 1, deep,
            filter: { ageMax: numOrNull(ageMax), favMin: numOrNull(favMin), viewsMin: numOrNull(viewsMin) },
            negative: negative.split(",").map((w) => w.trim()).filter(Boolean),
          },
        }),
      });
    },
    onSuccess: () => { setKeywords(""); qc.invalidateQueries({ queryKey: ["tasks"] }); },
  });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "cancel" | "run-now" | "delete" }) =>
      action === "delete" ? api(`/${id}`, { method: "DELETE" }) : api(`/${id}`, { method: "PATCH", body: JSON.stringify({ action }) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });

  const worker = data?.worker;
  const workerOnline = worker && Date.now() - Date.parse(worker.at) < 3 * 60000;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900"><ListChecks className="h-5 w-5 text-indigo-600" />Eklenti Görevleri</h1>
        <p className="mt-1 text-sm text-gray-500">
          Buradan verilen tarama ve analiz görevlerini, yan panelde Keşif sekmesindeki “Bu tarayıcı panelden görev alsın” açık olan eklenti (ör. VDS'teki) dakikada bir alıp çalıştırır.
        </p>
      </div>

      <div className={`mb-4 rounded-lg p-3 text-sm ${workerOnline ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
        {worker
          ? <>Görev alan eklenti {workerOnline ? "çevrimiçi" : "bir süredir görev sormadı"} · son kontrol: {ago(worker.at)}{worker.version ? ` · sürüm ${worker.version}` : ""}</>
          : "Henüz görev alan bir eklenti yok. VDS'teki tarayıcıda eklentinin yan panelinden Keşif sekmesine gir ve “Bu tarayıcı panelden görev alsın” kutusunu işaretle."}
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">Yeni tarama görevi</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="md:row-span-2">
            <span className={LABEL}>Anahtar kelimeler (her satıra bir tane)</span>
            <textarea value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={6} placeholder={"halloween shirt\nhalloween shirt funny"} className={INPUT} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label><span className={LABEL}>Kelime başına sayfa</span><input type="number" min={1} max={20} value={pages} onChange={(e) => setPages(e.target.value)} className={INPUT} /></label>
            <label><span className={LABEL}>Yaş en çok (gün)</span><input type="number" min={0} value={ageMax} onChange={(e) => setAgeMax(e.target.value)} className={INPUT} /></label>
            <label><span className={LABEL}>Favori en az</span><input type="number" min={0} value={favMin} onChange={(e) => setFavMin(e.target.value)} placeholder="—" className={INPUT} /></label>
            <label><span className={LABEL}>Görüntülenme/gün en az</span><input type="number" min={0} step="any" value={viewsMin} onChange={(e) => setViewsMin(e.target.value)} placeholder="—" className={INPUT} /></label>
          </div>
          <div className="space-y-3">
            <label className="block"><span className={LABEL}>Negatif kelimeler (virgülle)</span><input type="text" value={negative} onChange={(e) => setNegative(e.target.value)} placeholder="sweatshirt, hoodie, baby" className={INPUT} /></label>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
              <label className="flex items-center gap-2"><input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />Ön filtreye uyanların sayfasını aç (sepet / 24 saat)</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />Her gün tekrarla</label>
              {repeat && <label className="flex items-center gap-2">Saat <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" /></label>}
            </div>
          </div>
        </div>
        {create.error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(create.error as Error).message}</div>}
        <div className="mt-4 flex justify-end">
          <button onClick={() => create.mutate()} disabled={create.isPending || !keywords.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {repeat ? "Günlük görevi kur" : "Görevi ver"}
          </button>
        </div>
      </div>

      {act.error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{(act.error as Error).message}</div>}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" />Yükleniyor…</div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{(error as Error).message}</div>
      ) : !data?.tasks.length ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <ListChecks className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">Henüz görev yok. Yukarıdan bir tarama görevi ver; Ürün Keşfi sayfasından seçtiğin ürünleri de görev olarak analize gönderebilirsin.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.tasks.map((t) => {
            const [label, cls] = STATUS[t.status] ?? [t.status, "bg-gray-100 text-gray-600"];
            const p = t.progress;
            return (
              <div key={t.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">{t.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
                      {t.repeatDaily && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Her gün {new Date(t.runAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {t.status === "pending" ? `Sıradaki çalışma: ${when(t.runAt)}` : t.status === "running" ? `Başladı: ${when(t.startedAt ?? t.runAt)} · son bildirim: ${ago(t.heartbeatAt)}` : t.finishedAt ? `Bitti: ${when(t.finishedAt)}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {(t.status === "pending" || t.status === "failed" || t.status === "cancelled" || t.status === "done") && (
                      <button onClick={() => act.mutate({ id: t.id, action: "run-now" })} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Şimdi çalıştır</button>
                    )}
                    {(t.status === "pending" || t.status === "running") && (
                      <button onClick={() => act.mutate({ id: t.id, action: "cancel" })} title="İptal et" aria-label="İptal et" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => { if (confirm("Görev silinsin mi? (Kaydedilen keşif sonuçları silinmez.)")) act.mutate({ id: t.id, action: "delete" }); }}
                      title="Sil" aria-label="Görevi sil" className="rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                {p && (
                  <div className="mt-3 text-sm text-gray-700">
                    {p.message && <div>{p.message}</div>}
                    <div className="text-xs text-gray-500">
                      {p.kwTotal ? `Kelime ${p.kwIndex}/${p.kwTotal} · sayfa ${p.page}/${p.pages} · ` : ""}
                      {p.scanned != null ? `Taranan: ${p.scanned} · Kaydedilen: ${p.saved}` : ""}
                      {p.deepTotal ? ` · Açılan ürün: ${p.deepDone}/${p.deepTotal}` : ""}
                    </div>
                    {!!p.log?.length && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs font-medium text-gray-600">İşlem günlüğü</summary>
                        <div className="mt-1 max-h-48 overflow-auto rounded-lg bg-gray-50 p-2 font-mono text-xs text-gray-600">
                          {p.log.map((l, i) => <div key={i}>{l}</div>)}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                {t.error && <div className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">{t.error}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
